"use client";
import type { GameData } from "@/app/games/(details)/types";
import { usePassportProvider } from "@/context";
import { Action } from "@/context/AnalyticsProvider";
import { useGamePageAnalytics } from "@/context/AnalyticsProvider/gamePage";
import { useGameDetailsContext } from "@/context/GameDetailsContext";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { formatDateOnly } from "@/utils/date";
import { formatGenreLabel } from "@/utils/string";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import Footer from "../Footer";
import { BentoBox } from "./BentoBox/BentoBox";
import { FollowCTA } from "./FollowCTA";
import { GPOnboardingModal } from "./GPOnboarding/GPOnboardingModal";
import { getExperimentTypeFromSearchParams } from "./GPOnboarding/utils";
import { Quests } from "./RightPanel/Quests";
import { RightPanel } from "./RightPanel/RightPanel";
import { VideoIntro } from "./VideoIntro";
import { getPlatformButtons, getSocialButtons } from "./utils";

export const GamePageLayout = ({
  gameData,
}: {
  gameData: GameData;
}) => {
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const { newOnboardingFlow8BallNov25Gpt } = useFeatureFlags();

  const { isWishlisted } = useGameDetailsContext();

  const initialViewedRef = useRef(false);

  const { track } = useGamePageAnalytics();

  const { isLoggedIn } = usePassportProvider();

  const router = useRouter();
  const searchParams = useSearchParams();
  const experimentType = getExperimentTypeFromSearchParams(searchParams);

  useEffect(() => {
    if (searchParams?.get("loginRedirect") === "true") {
      setShowOnboardingModal(true);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("loginRedirect");
      router.replace(
        `${window.location.pathname}?${newSearchParams.toString()}`,
      );
    }
  }, [searchParams, router]);

  // Handle opening action URL after redirect from webview
  useEffect(() => {
    const openActionUrl = searchParams?.get("openActionUrl");
    if (openActionUrl) {
      try {
        const decodedUrl = decodeURIComponent(openActionUrl);
        // Open the action URL in a new tab
        window.open(decodedUrl, "_blank");
        // Remove the parameter from URL to prevent reopening on re-renders
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("openActionUrl");
        router.replace(
          `${window.location.pathname}?${newSearchParams.toString()}`,
        );
      } catch (error) {
        console.error("Error opening action URL:", error);
      }
    }
  }, [searchParams, router]);

  // Handle login action after redirect from webview
  useEffect(() => {
    const loginAction = searchParams?.get("loginAction");
    if (loginAction === "true") {
      // Show login modal
      setShowOnboardingModal(true);
      // Remove the parameter from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("loginAction");
      router.replace(
        `${window.location.pathname}?${newSearchParams.toString()}`,
      );
    }
  }, [searchParams, router]);

  useEffect(() => {
    if (isLoggedIn || !newOnboardingFlow8BallNov25Gpt || !experimentType)
      return;
    if (experimentType.includes("NoPrompt")) return;
    setShowOnboardingModal(true);
  }, [isLoggedIn, newOnboardingFlow8BallNov25Gpt, experimentType]);

  const closeOnboardingModal = useCallback(() => {
    setShowOnboardingModal(false);
  }, []);

  const openOnboardingModal = useCallback(() => {
    if (!newOnboardingFlow8BallNov25Gpt) return;
    setShowOnboardingModal(true);
  }, [newOnboardingFlow8BallNov25Gpt]);

  useEffect(() => {
    if (initialViewedRef.current) return;
    initialViewedRef.current = true;
    track({
      action: Action.VIEWED,
      extras: {
        control: "game-page",
      },
    });
  }, [track]);

  useEffect(() => {
    document.body.style.setProperty(
      "--accent-colour",
      gameData.gameContent?.accent_colour
        ?.replace("rgb(", "")
        .replace(")", "") || "0,255,242",
    );

    return () => {
      document.body.style.removeProperty("--accent-colour");
    };
  }, [gameData.gameContent?.accent_colour]);

  return (
    <>
      <GPOnboardingModal
        open={showOnboardingModal && newOnboardingFlow8BallNov25Gpt === true}
        onClose={closeOnboardingModal}
        experimentType={experimentType}
      />

      <VideoIntro gameData={gameData}>
        {/* Page Container */}
        <div className="min-h-screen flex flex-col flex-1 h-full justify-between px-[4vw] md:px-8 pb-52 md:pb-4 space-y-4">
          <div className="@container relative space-y-5 max-w-[108rem] flex flex-col items-center w-full mx-auto pt-10">
            {/* Header Container */}
            <div className="flex items-center justify-between w-full @3xl:gap-x-10">
              {/* Socials & Release/AMA Container */}
              <div className="space-y-5 flex flex-col items-start flex-1 max-w-[28rem] h-full justify-between relative">
                {/* Socials Glass Buttons */}
                <div className="@3xl:ml-2.5 flex flex-col items-center gap-y-2 @3xl:flex-row @3xl:gap-y-0 @3xl:gap-x-6 justify-start pb-16 @3xl:pb-0">
                  {getSocialButtons(gameData.details.social_stats)}
                </div>
                <div className="space-y-5 w-full">
                  <div className="@3xl:block hidden w-full h-px bg-white/10" />
                  <div className="@3xl:ml-2.5 space-y-0.5 font-suisse absolute bottom-0 left-0 @3xl:static">
                    <p className="text-xs leading-none text-neutral-50/60">
                      Release Date
                    </p>
                    <p className="text-neutral-50 whitespace-nowrap @md:text-base text-sm">
                      {gameData.gameContent?.release_date
                        ? formatDateOnly(
                            new Date(gameData.gameContent.release_date),
                          )
                        : "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Logo */}
              {gameData.details.logo_url ? (
                <Image
                  src={gameData.details.logo_url}
                  alt={`${gameData.details.name} Logo`}
                  width={466}
                  height={197}
                  className="object-contain max-w-[50%] @2xl:max-w-[40%] @3xl:max-w-[33%]"
                />
              ) : null}

              {/* Platforms & Categories Container */}
              <div className="space-y-5 flex flex-col items-end flex-1 max-w-[28rem] h-full justify-between relative">
                {/* Platforms Glass Buttons */}
                <div className="@3xl:mr-2.5 flex flex-col items-center gap-y-2 @3xl:flex-row @3xl:gap-y-0 @3xl:gap-x-6 justify-end pb-16 @3xl:pb-0">
                  {getPlatformButtons(gameData.gameContent?.store_urls)}
                </div>
                <div className="space-y-5 w-full">
                  <div className="@3xl:block hidden w-full h-px bg-white/10" />
                  <div className="@3xl:mr-2.5 space-y-0.5 font-suisse text-end absolute bottom-0 right-0 @3xl:static">
                    <p className="text-xs leading-none text-neutral-50/60">
                      Category
                    </p>
                    <p className="text-neutral-50 whitespace-nowrap @md:text-base text-sm">
                      {gameData.details?.genres
                        ?.map(formatGenreLabel)
                        .join(", ") ?? ""}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <FollowCTA
              gameData={gameData}
              showOnboardingModal={openOnboardingModal}
            />

            <BentoBox gameData={gameData} />

            {isWishlisted ? (
              <Quests
                gameData={gameData}
                className="md:hidden rounded-[1.25rem] glass-border relative pt-6 [&>div]:px-4 [&>li]:px-4 bg-black/30 backdrop-blur-[32px]"
              />
            ) : null}
          </div>
          <Footer className="!px-0" />
        </div>
        {/* Right Panel - Using flexbox, anchored to right edge */}
        <RightPanel
          className="h-screen sticky top-0 right-0 w-96"
          gameData={gameData}
          showOnboardingModal={openOnboardingModal}
        />
      </VideoIntro>
    </>
  );
};
